import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button, MultiSelect, Select, type MultiSelectOption } from '@knack/asterisk-react';

import { useSession } from '@/hooks/useSession';
import { shouldSendAnalytics } from '@/utils/analytics';
import {
  BASE_IMAGE_URL,
  SETUP_WIZARD_INDUSTRIES_USE_CASES,
  SETUP_WIZARD_STEPS
} from '@/pages/apps/create-app/constants';
import {
  goalsHorizontalStackUI,
  translationKeyPrefixHorizontalUI,
  translationKeyPrefixVariantTwo
} from '@/pages/apps/create-app/setup-wizard-variations/constants';
import {
  getTitleFromFilter,
  trackIndustryUseCaseEvent,
  updateIntercomIndustryUseCase,
  type AnalyticsIndustryUseCaseInterface
} from '@/pages/apps/create-app/setup-wizard-variations/helper';

interface StepSelectIndustryHorizontalUIProps {
  setCurrentStep: (step: string) => void;
  selectedIndustry: string | undefined;
  setSelectedIndustry: (value: string | undefined) => void;
  goals: MultiSelectOption[];
  setGoals: (value: MultiSelectOption[]) => void;
}

export function StepSelectIndustryHorizontalUI({
  setCurrentStep,
  selectedIndustry,
  setSelectedIndustry,
  goals,
  setGoals
}: StepSelectIndustryHorizontalUIProps) {
  const { industries } = SETUP_WIZARD_INDUSTRIES_USE_CASES;
  const [t] = useTranslation();
  const session = useSession();
  const [multiSelectOptions, setMultiSelectOptions] =
    useState<MultiSelectOption[]>(goalsHorizontalStackUI);

  return (
    <div
      className="flex w-full flex-col items-center justify-center gap-12 py-4 md:flex-row"
      data-testid="step-select-industry-horizontal"
    >
      <div className="flex w-full flex-col justify-center">
        <div className="mb-4 flex w-full items-center gap-2 text-emphasis sm:text-lg">
          <Trans i18nKey={`${translationKeyPrefixHorizontalUI}.step_1.industry`}>
            <Select
              onValueChange={(value) => {
                setSelectedIndustry(value);
              }}
              value={selectedIndustry}
            >
              <Select.Trigger
                size="lg"
                data-testid="industry-selector"
                placeholder={t(`${translationKeyPrefixHorizontalUI}.step_1.select_industry`)}
              />
              <Select.Content>
                {industries.map((industry) => (
                  <Select.Item key={industry} value={industry} data-testid={`industry-${industry}`}>
                    {getTitleFromFilter(t, industry)}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select>
          </Trans>
        </div>
        <div className="mb-4 flex flex-col text-emphasis sm:text-lg">
          <span className="mb-2">{t(`${translationKeyPrefixHorizontalUI}.step_1.goals`)}</span>

          <MultiSelect
            triggerClassName="w-full text-sm"
            contentClassName="w-full sm:min-w-[500px]"
            itemClassName="w-full"
            chipTextClassName="max-w-200"
            maxVisibleChips={2}
            virtualizerPaddingEnd={10}
            isSelectAllEnabled={false}
            placeholder={t(
              `${translationKeyPrefixHorizontalUI}.step_1.${selectedIndustry ? 'select_goals' : 'choose_industry_first'}`
            )}
            id="goals"
            selectedOptions={goals}
            onSelectOptions={(values) => {
              setGoals(values);
              setMultiSelectOptions((prevOptions) =>
                prevOptions.map((option) => ({
                  ...option
                }))
              );
            }}
            options={multiSelectOptions}
            disabled={!selectedIndustry}
          />
        </div>
        <div className="mt-4 flex w-full justify-start">
          <Button
            data-testid="next-button"
            disabled={!selectedIndustry || goals.length === 0}
            type="button"
            onClick={() => {
              const analyticsIndustryUseCaseBody: AnalyticsIndustryUseCaseInterface = {
                groupId: session.account.id,
                industry: selectedIndustry ?? '',
                intent: goals.map((goal) => goal.value).join(', ')
              };
              if (shouldSendAnalytics(session.user.email)) {
                trackIndustryUseCaseEvent(analyticsIndustryUseCaseBody);
              }
              if (selectedIndustry) {
                void updateIntercomIndustryUseCase(
                  getTitleFromFilter(t, selectedIndustry),
                  undefined,
                  analyticsIndustryUseCaseBody.intent
                );
              }
              setCurrentStep(SETUP_WIZARD_STEPS[1]);
            }}
          >
            {t('actions.next')}
          </Button>
        </div>
      </div>
      <div className="flex size-full">
        <img
          src={`${BASE_IMAGE_URL}step1-illustration-2.png`}
          alt={t(`${translationKeyPrefixVariantTwo}.step_1.title`)}
        />
      </div>
    </div>
  );
}
